import React, { useEffect, useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import MaraSportsLogo from '../components/MaraSportsLogo';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { Trophy, CheckCircle, Loader2, XCircle, ArrowRight } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PaymentSuccessPage = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const { token, isAuthenticated, loading: authLoading } = useAuth();
    const [status, setStatus] = useState('checking'); // checking, success, failed, expired
    const [paymentData, setPaymentData] = useState(null);

    useEffect(() => {
        if (authLoading) return;
        
        if (!isAuthenticated) {
            navigate('/auth');
            return;
        }

        const sessionId = searchParams.get('session_id');
        if (sessionId && token) {
            checkPaymentStatus(sessionId);
        } else {
            setStatus('failed');
        }
    }, [searchParams, token, isAuthenticated, authLoading]);

    const checkPaymentStatus = async (sessionId) => {
        let attempts = 0;
        const maxAttempts = 10;
        const pollInterval = 2000;

        const poll = async () => {
            if (attempts >= maxAttempts) {
                setStatus('failed');
                return;
            }

            try {
                const response = await axios.get(
                    `${API_URL}/api/payments/status/${sessionId}`,
                    { headers: { Authorization: `Bearer ${token}` } }
                );

                if (response.data.payment_status === 'paid') {
                    setStatus('success');
                    setPaymentData(response.data);
                    return;
                } else if (response.data.status === 'expired') {
                    setStatus('expired');
                    return;
                }

                // Continue polling
                attempts++;
                setTimeout(poll, pollInterval);
            } catch (error) {
                console.error('Error checking payment status:', error);
                attempts++;
                setTimeout(poll, pollInterval);
            }
        };

        poll();
    };

    if (authLoading || status === 'checking') {
        return (
            <div className="min-h-screen bg-[#0a1628] flex items-center justify-center text-white">
                <div className="text-center">
                    <Loader2 className="w-16 h-16 text-primary animate-spin mx-auto mb-4" />
                    <h2 className="font-heading text-2xl uppercase mb-2">Verifying Payment</h2>
                    <p className="text-muted-foreground">Please wait while we confirm your payment...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-[#0a1628] text-white">
            <div className="fixed inset-0 grid-lines pointer-events-none z-0" />

            <header className="sticky top-0 z-30 bg-[#0a1628]/80 backdrop-blur-lg border-b border-white/5">
                <div className="max-w-[1400px] mx-auto px-6 md:px-12">
                    <div className="flex items-center justify-center h-16">
                        <Link to="/" className="flex items-center gap-2">
                            <MaraSportsLogo className="h-8" />
                            <span className="font-heading font-bold text-lg tracking-tight uppercase">
                                NextGen<span className="text-primary"> Tournaments</span>
                            </span>
                        </Link>
                    </div>
                </div>
            </header>

            <main className="relative z-10 max-w-[600px] mx-auto px-6 py-20">
                {status === 'success' && (
                    <div className="bg-[#0d2847] border border-white/5 p-8 text-center">
                        <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
                            <CheckCircle className="w-12 h-12 text-green-400" />
                        </div>
                        <h1 className="font-heading font-black text-3xl uppercase mb-4">
                            Payment Successful!
                        </h1>
                        <p className="text-muted-foreground mb-6">
                            Your registration has been confirmed. A confirmation email has been sent to your registered email address.
                        </p>
                        
                        {paymentData && (
                            <div className="bg-[#0a1628] p-4 border border-white/10 mb-6 text-left">
                                <div className="space-y-2 text-sm">
                                    <div className="flex justify-between">
                                        <span className="text-muted-foreground">Amount Paid</span>
                                        <span className="font-mono">
                                            {paymentData.currency === 'USD' ? '$' : 'KSh '}
                                            {paymentData.amount?.toLocaleString()}
                                        </span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-muted-foreground">Registrations Confirmed</span>
                                        <span>{paymentData.registration_ids?.length || 0}</span>
                                    </div>
                                    <div className="border-t border-white/10 pt-2 mt-2">
                                        <p className="text-xs text-muted-foreground uppercase mb-1">Notifications Sent:</p>
                                        <div className="flex justify-between text-green-400">
                                            <span>Email Confirmation</span>
                                            <span>{paymentData.confirmation_email_sent ? '✓ Sent' : '—'}</span>
                                        </div>
                                        <div className="flex justify-between text-green-400">
                                            <span>SMS Confirmation</span>
                                            <span>{paymentData.confirmation_sms_sent ? '✓ Sent' : '—'}</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        <div className="flex flex-col sm:flex-row gap-3 justify-center">
                            <Link to="/dashboard">
                                <Button className="bg-primary hover:bg-red-600 text-white uppercase tracking-widest">
                                    View My Registrations
                                    <ArrowRight className="w-4 h-4 ml-2" />
                                </Button>
                            </Link>
                            <Link to="/tournaments">
                                <Button variant="outline" className="border-white/20 hover:bg-white/5 uppercase tracking-widest">
                                    Browse More Tournaments
                                </Button>
                            </Link>
                        </div>
                    </div>
                )}

                {status === 'failed' && (
                    <div className="bg-[#0d2847] border border-white/5 p-8 text-center">
                        <div className="w-20 h-20 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
                            <XCircle className="w-12 h-12 text-red-400" />
                        </div>
                        <h1 className="font-heading font-black text-3xl uppercase mb-4">
                            Payment Failed
                        </h1>
                        <p className="text-muted-foreground mb-6">
                            We couldn't verify your payment. Your cart items have been preserved.
                        </p>
                        <div className="flex flex-col sm:flex-row gap-3 justify-center">
                            <Link to="/cart">
                                <Button className="bg-primary hover:bg-red-600 text-white uppercase tracking-widest">
                                    Try Again
                                </Button>
                            </Link>
                            <Link to="/dashboard">
                                <Button variant="outline" className="border-white/20 hover:bg-white/5 uppercase tracking-widest">
                                    Go to Dashboard
                                </Button>
                            </Link>
                        </div>
                    </div>
                )}

                {status === 'expired' && (
                    <div className="bg-[#0d2847] border border-white/5 p-8 text-center">
                        <div className="w-20 h-20 bg-yellow-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
                            <XCircle className="w-12 h-12 text-yellow-400" />
                        </div>
                        <h1 className="font-heading font-black text-3xl uppercase mb-4">
                            Session Expired
                        </h1>
                        <p className="text-muted-foreground mb-6">
                            Your payment session has expired. Please try again.
                        </p>
                        <Link to="/cart">
                            <Button className="bg-primary hover:bg-red-600 text-white uppercase tracking-widest">
                                Return to Cart
                            </Button>
                        </Link>
                    </div>
                )}
            </main>
        </div>
    );
};

export default PaymentSuccessPage;
